import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineRounded";
import InboxIcon from "@mui/icons-material/InboxOutlined";

const centered = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  gap: 1.5,
  py: 8,
  px: 3,
} as const;

export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return (
    <Box sx={centered} role="status" aria-live="polite">
      <CircularProgress size={28} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/** Row skeletons for a table body while data loads. */
export function TableLoadingRows({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <Stack spacing={1} sx={{ p: 2 }}>
      {Array.from({ length: rows }, (_row, r) => (
        <Stack key={r} direction="row" spacing={2}>
          {Array.from({ length: cols }, (_col, c) => (
            <Skeleton key={c} variant="rounded" height={20} sx={{ flex: c === 0 ? 2 : 1 }} />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

export function EmptyState({
  title,
  description,
  icon,
  action,
}: {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Box sx={centered}>
      <Box sx={{ color: "text.disabled", "& svg": { fontSize: 40 } }}>{icon ?? <InboxIcon />}</Box>
      <Typography variant="h4">{title}</Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          {description}
        </Typography>
      ) : null}
      {action ? <Box sx={{ mt: 1 }}>{action}</Box> : null}
    </Box>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "Something went wrong";
  return (
    <Box sx={centered}>
      <ErrorOutlineIcon color="error" sx={{ fontSize: 40 }} />
      <Typography variant="h4">Couldn’t load this</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
        {message}
      </Typography>
      {onRetry ? (
        <Button onClick={onRetry} variant="outlined" sx={{ mt: 1 }}>
          Retry
        </Button>
      ) : null}
    </Box>
  );
}
